export const formatJobContent = ({
  content,
  isPreview,
}: {
  content: string;
  isPreview: boolean;
}): string => {
  if (!content) return '';

  // Clean up and normalize the content first
  let formattedContent = content
    // Remove any HTML tags that might already exist
    .replace(/<[^>]*>/g, '')
    // Convert escaped newlines from Notion API to actual newlines
    .replace(/\\n/g, '\n')
    // Handle Unicode escape sequences (like \u0026 for &)
    .replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
      return String.fromCharCode(parseInt(hex, 16));
    })
    // Handle common escaped characters
    .replace(/\\&/g, '&')
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, '\\')
    .replace(/\\t/g, ' ')
    .replace(/\\r/g, '')
    .replace(/\\f/g, '')
    .replace(/\\b/g, '')
    .replace(/\\v/g, ' ')
    // Handle HTML entities that might come from Notion
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    // Normalize line breaks
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Remove extra whitespace at the beginning and end of lines
    .replace(/^[ \t]+|[ \t]+$/gm, '')
    // Remove multiple consecutive empty lines
    .replace(/\n{3,}/g, '\n\n')
    // Clean up special characters from Notion (various Unicode spaces)
    .replace(/[\u00A0\u2000-\u200B\u2028\u2029\u202F\u205F\u3000]/g, ' ')
    // Clean up other problematic Unicode characters that might cause formatting issues
    .replace(/\u200C/g, '') // Zero-width non-joiner
    .replace(/\u200D/g, '') // Zero-width joiner
    .replace(/\u200E/g, '') // Left-to-right mark
    .replace(/\u200F/g, '') // Right-to-left mark
    .replace(/[\uFEFF]/g, '') // Byte Order Mark
    // Trim the entire content
    .trim();

  // Convert markdown-like formatting to HTML
  formattedContent = formattedContent
    // Handle headers (h1-h4)
    .replace(
      /^#### (.+)$/gm,
      `<h4 class="text-base font-semibold ${isPreview ? '' : 'mt-4 mb-2'}">$1</h4>`,
    )
    .replace(
      /^### (.+)$/gm,
      `<h3 class="text-lg font-semibold ${isPreview ? '' : 'mt-6 mb-3'}">$1</h3>`,
    )
    .replace(
      /^## (.+)$/gm,
      `<h2 class="text-xl font-semibold ${isPreview ? '' : 'mt-6 mb-3'}">$1</h2>`,
    )
    .replace(
      /^# (.+)$/gm,
      `<h1 class="text-2xl font-bold ${isPreview ? '' : 'mt-8 mb-4'}">$1</h1>`,
    )

    // Handle different bullet point formats
    .replace(/^[\u2022\u2023\u25E6\u2043\u2219][ \t]+(.+)$/gm, '• $1') // Convert different bullet characters to standard bullet
    .replace(/^[*-][ \t]+(.+)$/gm, '• $1') // Convert * and - to bullet points
    .replace(/^\d+\.[ \t]+(.+)$/gm, '• $1') // Convert numbered lists to bullet points
    // Handle Notion's specific bullet format (starts with • after processing)
    .replace(/^•[ \t]*(.+)$/gm, '• $1')

    // Handle bold and italic text
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em class="italic">$1</em>')
    .replace(/__(.+?)__/g, '<strong class="font-semibold">$1</strong>')
    .replace(/_(.+?)_/g, '<em class="italic">$1</em>')

    // Handle code blocks and inline code
    .replace(/```[\s\S]*?```/g, (match) => {
      const code = match.replace(/```/g, '').trim();
      return `<pre class="bg-gray-100 p-3 rounded text-sm mb-4 overflow-x-auto"><code>${code}</code></pre>`;
    })
    .replace(
      /`([^`]+)`/g,
      '<code class="bg-gray-100 px-2 py-1 rounded text-sm">$1</code>',
    );

  // Split content into sections based on headers and handle bullet points
  const sections = formattedContent.split(/(?=^<h[1-4])/gm);
  const processedSections = sections.map((section) => {
    if (!section.trim()) return '';

    // Check if this section starts with a header
    const isHeaderSection = section.trim().match(/^<h[1-4]/);

    if (isHeaderSection) {
      // Extract header and content
      const lines = section.trim().split('\n');
      const header = lines[0];
      const contentLines = lines.slice(1);

      // Process content after header
      const { bulletPoints, nonBulletLines } = processLines(contentLines);

      let result = header;

      // Add non-bullet content
      if (nonBulletLines.length > 0) {
        const joinedContent = nonBulletLines.join(' ').trim();
        if (joinedContent) {
          result += `<p class="mb-4">${joinedContent}</p>`;
        }
      }

      // Add bullet points as a list
      if (bulletPoints.length > 0) {
        result += `<ul class="list-disc ml-4 space-y-1 mb-4">${bulletPoints.join('')}</ul>`;
      }

      return result;
    } else {
      // Process regular content section
      const paragraphs = section.split(/\n\s*\n/);
      return paragraphs
        .map((paragraph) => {
          const lines = paragraph.trim().split('\n');
          const { bulletPoints, nonBulletLines } = processLines(lines);

          let result = '';

          // Add non-bullet content
          if (nonBulletLines.length > 0) {
            const joinedContent = nonBulletLines.join(' ').trim();
            if (joinedContent) {
              result += `<p class="mb-4">${joinedContent}</p>`;
            }
          }

          // Add bullet points as a list
          if (bulletPoints.length > 0) {
            result += `<ul class="list-disc ml-4 space-y-1 mb-4">${bulletPoints.join('')}</ul>`;
          }

          return result;
        })
        .filter((p) => p.trim())
        .join('');
    }
  });

  return processedSections.filter((s) => s.trim()).join('');
};

// Helper function to process lines and separate bullets from regular content
const processLines = (
  lines: string[],
): { bulletPoints: string[]; nonBulletLines: string[] } => {
  const bulletPoints: string[] = [];
  const nonBulletLines: string[] = [];

  lines.forEach((line) => {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith('•')) {
      bulletPoints.push(
        `<li class="ml-4">${trimmedLine.substring(1).trim()}</li>`,
      );
    } else if (
      trimmedLine &&
      !trimmedLine.match(/^<h[1-4]|^<pre|^<code|^<ul|^<li/)
    ) {
      nonBulletLines.push(trimmedLine);
    }
  });

  return { bulletPoints, nonBulletLines };
};

export const isNewJob = (dateStr: string): boolean => {
  // Use UTC to ensure consistent behavior between server and client
  const jobDate = new Date(dateStr);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setUTCDate(thirtyDaysAgo.getUTCDate() - 30);
  return jobDate > thirtyDaysAgo;
};

export const formatDate = (dateStr: string): string => {
  // Use UTC to ensure consistent behavior between server and client
  const date = new Date(dateStr);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
};
